"use client";

import { useEffect, useMemo, useState } from "react";

const promptText = "生成一个AIPC发展趋势的PPT";
const editorVideoUrl = "https://image.yoojober.com/upload-m/2026-04/69f2c7437f343.mp4";
const streamingFrames = ["AI 生成中", "AI 生成中.", "AI 生成中..", "AI 生成中..."];
type Phase = "typing" | "processing" | "video" | "fading";

export function AnimatedHeroEditor() {
  const [phase, setPhase] = useState<Phase>("typing");
  const [typedLength, setTypedLength] = useState(0);
  const [streamFrame, setStreamFrame] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setReducedMotion(mediaQuery.matches);

    syncPreference();
    mediaQuery.addEventListener("change", syncPreference);

    return () => mediaQuery.removeEventListener("change", syncPreference);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setPhase("video");
      setTypedLength(promptText.length);
      setStreamFrame(streamingFrames.length - 1);
      return;
    }

    let typeInterval: number | undefined;
    let streamInterval: number | undefined;
    let processingTimeout: number | undefined;
    let videoTimeout: number | undefined;
    let fadeTimeout: number | undefined;
    let restartTimeout: number | undefined;

    setPhase("typing");
    setTypedLength(0);
    setStreamFrame(0);

    typeInterval = window.setInterval(() => {
      setTypedLength((current) => {
        if (current >= promptText.length) {
          if (typeInterval) {
            window.clearInterval(typeInterval);
          }

          processingTimeout = window.setTimeout(() => {
            setPhase("processing");
            streamInterval = window.setInterval(() => {
              setStreamFrame((currentFrame) => (currentFrame + 1) % streamingFrames.length);
            }, 240);
          }, 260);

          videoTimeout = window.setTimeout(() => {
            setPhase("video");
            if (streamInterval) {
              window.clearInterval(streamInterval);
            }
          }, 2100);

          fadeTimeout = window.setTimeout(() => {
            setPhase("fading");
          }, 7800);

          restartTimeout = window.setTimeout(() => {
            setPhase("typing");
            setTypedLength(0);
            setStreamFrame(0);
            setCycleKey((current) => current + 1);
          }, 8600);

          return current;
        }

        return current + 1;
      });
    }, 72);

    return () => {
      if (typeInterval) {
        window.clearInterval(typeInterval);
      }
      if (streamInterval) {
        window.clearInterval(streamInterval);
      }
      if (processingTimeout) {
        window.clearTimeout(processingTimeout);
      }
      if (videoTimeout) {
        window.clearTimeout(videoTimeout);
      }
      if (fadeTimeout) {
        window.clearTimeout(fadeTimeout);
      }
      if (restartTimeout) {
        window.clearTimeout(restartTimeout);
      }
    };
  }, [cycleKey, reducedMotion]);

  const typedText = useMemo(() => promptText.slice(0, typedLength), [typedLength]);
  const isProcessing = phase === "processing";
  const showVideo = phase === "video" || phase === "fading";
  const showIntro = phase === "typing" || phase === "processing";

  return (
    <div className="hero-editor-shell relative w-full max-w-[560px] overflow-hidden rounded-[1.9rem] border border-white/75 bg-[linear-gradient(180deg,rgba(245,246,250,0.9)_0%,rgba(241,244,250,0.74)_100%)] p-4 text-[#122033] shadow-[0_24px_60px_rgba(26,44,91,0.1)] sm:p-5 lg:max-w-[660px] lg:p-5 xl:max-w-[720px] xl:p-6">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(155,110,245,0.18),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(91,110,245,0.12),transparent_32%)]" />
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-[linear-gradient(90deg,rgba(91,110,245,0),rgba(91,110,245,0.4),rgba(155,110,245,0))]" />

      <div className="relative flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#64748b]">AI 文档工作台</div>
            <div className="mt-2 text-[1.05rem] font-semibold tracking-[-0.02em] text-[#0f172a]">
              从一句指令到编辑器演示
            </div>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-medium text-[#5f6b7c] shadow-[0_10px_20px_rgba(18,32,51,0.06)]">
            <span className="h-2 w-2 rounded-full bg-[linear-gradient(135deg,#5B6EF5,#9B6EF5)] shadow-[0_0_0_5px_rgba(91,110,245,0.08)]" />
            PPT生成
          </div>
        </div>

        <div className="relative overflow-hidden rounded-[1.55rem] border border-white/70 bg-white/78 p-4 shadow-[0_16px_40px_rgba(18,32,51,0.06)] sm:p-5 lg:p-6">
          <div className="absolute inset-x-4 top-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ffb4b4]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#ffd980]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#8ad7a0]" />
            </div>
            <div className="rounded-full bg-[#f3f6fc] px-3 py-1 text-[11px] font-medium text-[#6b7280]">智能 PPT 编辑器</div>
          </div>

          <div className="relative min-h-[320px] pt-12 sm:min-h-[360px] lg:min-h-[400px]">
            <div
              className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                showIntro ? "relative translate-y-0 opacity-100" : "pointer-events-none absolute inset-x-0 top-10 -translate-y-6 opacity-0"
              }`}
            >
              <div className="rounded-[1.3rem] bg-[#fbfcfe] p-4 sm:p-5">
                <div className="text-[0.98rem] font-semibold tracking-[-0.02em] text-[#0f172a] sm:text-[1.02rem]">
                  一句指令，自动生成可编辑 PPT
                </div>
                <div className="mt-4 rounded-[1.15rem] border border-[#e8eef7] bg-white px-4 py-3 text-[15px] leading-7 text-[#0f172a] shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] sm:px-5 sm:py-4 sm:text-base sm:leading-8">
                  <span>{typedText}</span>
                  {phase === "typing" ? <span className="hero-editor-caret ml-0.5" aria-hidden="true" /> : null}
                </div>

                <div className="mt-4 rounded-[1.15rem] border border-[#e8edf8] bg-white px-4 py-3 sm:px-5 sm:py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#718096]">生成状态</div>
                      <div className="mt-2 flex items-center gap-3 text-sm font-medium text-[#1e293b]">
                        <span className="hero-editor-pulse h-2.5 w-2.5 rounded-full bg-[linear-gradient(135deg,#5B6EF5,#9B6EF5)]" />
                        <span>{isProcessing ? streamingFrames[streamFrame] : "等待生成"}</span>
                      </div>
                    </div>
                    <div className="rounded-full bg-[#f2f5fb] px-3 py-1 text-[11px] font-medium text-[#667085]">
                      {isProcessing ? "处理中" : "待开始"}
                    </div>
                  </div>
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#e8edf7]">
                    <div
                      className={`hero-editor-progress h-full rounded-full bg-[linear-gradient(90deg,#5B6EF5,#9B6EF5)] ${
                        isProcessing ? "is-processing" : showVideo ? "is-complete" : ""
                      }`}
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3 rounded-[1rem] bg-[#f3f6fc] px-4 py-3 text-sm text-[#61708a] sm:px-5 sm:py-4 sm:text-[0.95rem]">
                  <span className="hero-editor-status-dot h-2.5 w-2.5 rounded-full bg-[linear-gradient(135deg,#5B6EF5,#9B6EF5)]" />
                  <span>即将跳转编辑器...</span>
                </div>
              </div>
            </div>

            <div
              className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                showVideo ? "relative translate-y-0 opacity-100" : "pointer-events-none absolute inset-x-0 top-10 translate-y-8 opacity-0"
              }`}
            >
              <div className="overflow-hidden rounded-[1.3rem] bg-[#fafbfd] shadow-[0_18px_42px_rgba(17,24,39,0.08)]">
                <div className="flex items-center justify-between border-b border-[#ebeff7] bg-white/88 px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold text-[#0f172a]">PPT 编辑器预览</div>
                  </div>
                  <div className="rounded-full bg-[linear-gradient(135deg,#5B6EF5,#9B6EF5)] px-3 py-1 text-[10px] font-medium text-white">
                    编辑器实录
                  </div>
                </div>

                <div className="relative bg-[#ece7df]">
                  <video
                    key={cycleKey}
                    className="block aspect-[16/9] min-h-[220px] w-full bg-[#ece7df] object-cover sm:min-h-[260px] lg:min-h-[300px]"
                    src={editorVideoUrl}
                    autoPlay
                    muted
                    playsInline
                    loop
                    preload="metadata"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#fafbfd] via-[#fafbfd]/30 to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-editor-shell {
          backdrop-filter: saturate(1.08);
        }

        .hero-editor-caret {
          display: inline-block;
          width: 1px;
          height: 1.05em;
          background: #5b6ef5;
          vertical-align: -0.12em;
          animation: hero-editor-caret-blink 1s ease-in-out infinite;
        }

        .hero-editor-pulse {
          animation: hero-editor-pulse 1.8s ease-in-out infinite;
        }

        .hero-editor-status-dot {
          animation: hero-editor-status-dot 1.4s ease-in-out infinite;
          box-shadow: 0 0 0 0 rgba(91, 110, 245, 0.18);
        }

        .hero-editor-progress {
          width: 100%;
          transform-origin: left center;
          transform: scaleX(0);
        }

        .hero-editor-progress.is-processing {
          animation: hero-editor-progress-fill 1.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .hero-editor-progress.is-complete {
          transform: scaleX(1);
        }

        @keyframes hero-editor-caret-blink {
          0%,
          45% {
            opacity: 1;
          }
          55%,
          100% {
            opacity: 0;
          }
        }

        @keyframes hero-editor-pulse {
          0%,
          100% {
            opacity: 0.75;
            transform: scale(0.9);
            box-shadow: 0 0 0 0 rgba(91, 110, 245, 0.18);
          }
          50% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 8px rgba(91, 110, 245, 0);
          }
        }

        @keyframes hero-editor-status-dot {
          0%,
          100% {
            opacity: 0.7;
            transform: scale(0.92);
            box-shadow: 0 0 0 0 rgba(91, 110, 245, 0.16);
          }
          50% {
            opacity: 1;
            transform: scale(1);
            box-shadow: 0 0 0 6px rgba(91, 110, 245, 0);
          }
        }

        @keyframes hero-editor-progress-fill {
          0% {
            transform: scaleX(0.08);
          }
          100% {
            transform: scaleX(1);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-editor-caret,
          .hero-editor-pulse,
          .hero-editor-status-dot,
          .hero-editor-progress.is-processing {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
